import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { adminRequest } from '../../utils/adminRequest';
import { getImageSrc } from '../../utils/request';

function AdminMembers() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [keyword, setKeyword] = useState(() => searchParams.get('keyword') || '');
  const [loading, setLoading] = useState(true);

  const fetchList = useCallback(async (p, kw) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(limit) });
      if (kw.trim()) params.set('keyword', kw.trim());
      const res = await adminRequest({ url: `/api/admin/members?${params}`, method: 'GET' });
      const d = res.data || {};
      setList(d.list || []);
      setTotal(d.total ?? 0);
      setPage(d.page ?? p);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin');
      else alert(err.response?.data?.error || '获取会员列表失败');
    } finally {
      setLoading(false);
    }
  }, [limit, navigate]);

  useEffect(() => {
    const p = parseInt(searchParams.get('page'), 10) || 1;
    const kw = searchParams.get('keyword') || '';
    setKeyword(kw);
    fetchList(p, kw);
  }, [searchParams, fetchList]);

  const applySearch = (e) => {
    e?.preventDefault();
    const next = new URLSearchParams();
    if (keyword.trim()) next.set('keyword', keyword.trim());
    next.set('page', '1');
    setSearchParams(next);
  };

  const goPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    if (!keyword.trim()) next.delete('keyword');
    else next.set('keyword', keyword.trim());
    setSearchParams(next);
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`确定删除会员「${row.nickname || row.id}」（ID ${row.id}）吗？将同时删除其牵线记录、名额、订单等关联数据。`)) return;
    try {
      await adminRequest({ url: `/api/admin/members/${row.id}`, method: 'DELETE' });
      fetchList(page, keyword);
    } catch (err) {
      alert(err.response?.data?.error || '删除失败');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>会员管理</h2>
        <Link
          to="/admin/members/new"
          style={{
            padding: '10px 18px',
            background: '#6366f1',
            color: '#fff',
            borderRadius: 8,
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: 15
          }}
        >
          ＋ 新增会员
        </Link>
      </div>
      {loading && <div style={{ marginBottom: 12, color: '#6366f1' }}>加载中...</div>}
      <form
        onSubmit={applySearch}
        style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20, alignItems: 'center' }}
      >
        <input
          type="search"
          placeholder="搜索 ID / 昵称 / openid / 微信"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          style={{
            flex: '1 1 240px',
            minWidth: 200,
            padding: '10px 12px',
            border: '1px solid #ddd',
            borderRadius: 8,
            fontSize: 15
          }}
        />
        <button
          type="submit"
          style={{
            padding: '10px 20px',
            background: '#6366f1',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          搜索
        </button>
      </form>

      <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #eee', textAlign: 'left', background: '#fafafa' }}>
              <th style={{ padding: 12 }}>ID</th>
              <th style={{ padding: 12 }}>头像</th>
              <th style={{ padding: 12 }}>昵称</th>
              <th style={{ padding: 12 }}>性别</th>
              <th style={{ padding: 12 }}>年龄</th>
              <th style={{ padding: 12 }}>城市</th>
              <th style={{ padding: 12 }}>状态</th>
              <th style={{ padding: 12 }}>注册时间</th>
              <th style={{ padding: 12, width: 180 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: 12 }}>{u.id}</td>
                <td style={{ padding: 12 }}>
                  {u.avatar ? (
                    <a
                      href={getImageSrc(u.avatar)}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'inline-block', lineHeight: 0 }}
                      title="点击查看原图"
                    >
                      <img
                        src={getImageSrc(u.avatar)}
                        alt={`${u.nickname || u.id} 头像`}
                        style={{
                          width: 52,
                          height: 52,
                          objectFit: 'cover',
                          borderRadius: 10,
                          border: '1px solid #e5e7eb',
                          background: '#f8fafc',
                          display: 'block'
                        }}
                      />
                    </a>
                  ) : (
                    <div
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 10,
                        border: '1px dashed #d1d5db',
                        background: '#f9fafb',
                        color: '#9ca3af',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12
                      }}
                    >
                      无图
                    </div>
                  )}
                </td>
                <td style={{ padding: 12 }}>
                  <div style={{ fontWeight: 600 }}>{u.nickname || '-'}</div>
                  <div style={{ color: '#888', fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>{u.openid}</div>
                </td>
                <td style={{ padding: 12 }}>{u.gender === 'male' ? '男' : u.gender === 'female' ? '女' : u.gender || '-'}</td>
                <td style={{ padding: 12 }}>{u.age ?? '-'}</td>
                <td style={{ padding: 12 }}>{u.city || '-'}</td>
                <td style={{ padding: 12 }}>{u.status || '-'}</td>
                <td style={{ padding: 12 }}>{u.created_at ? new Date(u.created_at).toLocaleString('zh-CN') : '-'}</td>
                <td style={{ padding: 12 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Link
                      to={`/admin/members/${u.id}/edit`}
                      style={{
                        padding: '6px 12px',
                        background: '#e0e7ff',
                        color: '#6366f1',
                        borderRadius: 6,
                        textDecoration: 'none',
                        fontSize: 13
                      }}
                    >
                      查看 / 编辑
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(u)}
                      style={{
                        padding: '6px 12px',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 13
                      }}
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>暂无数据</div>
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ color: '#666' }}>共 {total} 人</span>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => goPage(page - 1)}
            style={{
              padding: '8px 14px',
              border: '1px solid #ddd',
              borderRadius: 6,
              background: '#fff',
              cursor: page <= 1 ? 'not-allowed' : 'pointer'
            }}
          >
            上一页
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => goPage(page + 1)}
            style={{
              padding: '8px 14px',
              border: '1px solid #ddd',
              borderRadius: 6,
              background: '#fff',
              cursor: page >= totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            下一页
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminMembers;
